import { Box, Dialog, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useFloatingBubbles } from "src/context/floating-bubbles/FloatingBubblesContext";
import { formatUSPhone } from "src/utils";
import { useOptionalChatDock } from "@components/chat-dock";
import { getDisplayName, findResolvedByPhone } from "@components/virtual-receptionist/contacts/types/bulk-resolver.types";
import { highlightText } from "@components/shared/utils/highlightText";
import { useGlobalMessageSearch } from "./useGlobalMessageSearch";
import { GlobalSearchResultSection } from "./GlobalSearchResultSection";

interface GlobalSearchModalProps {
  open: boolean;
  onClose: () => void;
}

function getSenderDisplayName(entity: any): string {
  if (!entity) return "Unknown";
  return entity.businessName || `${entity.firstName ?? ""} ${entity.lastName ?? ""}`.trim() || entity.email || "Unknown";
}

function getInternalMessagePreview(message: any): string {
  const content = message?.content;
  if (content?.text) return String(content.text);
  if (content?.name) return `File: ${content.name}`;
  return "Message";
}

function getSmsContactLabels(message: any, bulkResolvedMap: Record<string, any>): { name: string | null; phone: string } {
  const phoneNumber = message.direction === "outgoing" ? message.to : message.from;
  const resolved = findResolvedByPhone(bulkResolvedMap, phoneNumber);
  const name = getDisplayName(resolved);
  const phone = formatUSPhone(phoneNumber) || phoneNumber;
  return { name, phone };
}

function getSmsMessagePreview(message: any): string {
  const body = message?.content?.Body?.trim();
  if (body) return body;

  const mediaUrls: string[] = message?.content?.MediaUrl ?? [];
  if (mediaUrls.length === 1) return "Media message";
  if (mediaUrls.length > 1) return `${mediaUrls.length} media attachments`;
  return "Empty message";
}

export function GlobalSearchModal({ open, onClose }: GlobalSearchModalProps) {
  const chatDock = useOptionalChatDock();
  const { openCalls } = useFloatingBubbles();
  const { query, setQuery, trimmedQuery, isEnabled, isMine, internal, sms } = useGlobalMessageSearch();

  const handleSelectInternal = (message: any) => {
    const other = isMine(message.sender) ? message.receiver : message.sender;
    if (!other || !chatDock) return;

    openCalls();
    chatDock.openChat(
      { type: "internal", contactId: String(other.uid ?? other.id), contact: null },
      { chatName: getSenderDisplayName(other), focusMessageUid: message.uid },
    );
    onClose();
  };

  const handleSelectSms = (message: any) => {
    const contactPhoneNumber = message.direction === "outgoing" ? message.to : message.from;
    const sessionNumber = message.direction === "outgoing" ? message.from : message.to;
    if (!contactPhoneNumber || !chatDock) return;

    const { name, phone } = getSmsContactLabels(message, sms.bulkResolvedMap);

    openCalls();
    chatDock.openChat(
      { type: "sms", phoneNumber: contactPhoneNumber, companyId: message.companyId, sessionNumber, contact: null },
      { chatName: name || phone, focusMessageUid: message.uid },
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" sx={{ zIndex: 2147483000 }}>
      <Box p={2}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search internal messages and SMS..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {!isEnabled && (
          <Typography variant="body2" color="text.secondary">
            Type to search across internal messages and SMS.
          </Typography>
        )}

        {isEnabled && (
          <Box display="flex" flexDirection="row" gap={0} maxHeight="60vh">
            <Box flex={1} minWidth={0} pr={2} overflow="auto">
              <GlobalSearchResultSection
                title="Internal messages"
                results={internal.results}
                isLoading={internal.isLoading}
                isFetchingNextPage={internal.isFetchingNextPage}
                hasNextPage={internal.hasNextPage}
                error={internal.error}
                onFetchNextPage={internal.fetchNextPage}
                getResultKey={(message: any) => message.uid}
                renderResult={(message: any) => (
                  <Box
                    onClick={() => handleSelectInternal(message)}
                    sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider", cursor: "pointer", "&:hover": { bgcolor: "grey.50" } }}
                  >
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      {getSenderDisplayName(isMine(message.sender) ? message.receiver : message.sender)}
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {highlightText(getInternalMessagePreview(message), trimmedQuery)}
                    </Typography>
                  </Box>
                )}
              />
            </Box>

            <Box borderLeft="1px solid" borderColor="divider" />

            <Box flex={1} minWidth={0} pl={2} overflow="auto">
              <GlobalSearchResultSection
                title="SMS"
                results={sms.results}
                isLoading={sms.isLoading}
                isFetchingNextPage={sms.isFetchingNextPage}
                hasNextPage={sms.hasNextPage}
                error={sms.error}
                onFetchNextPage={sms.fetchNextPage}
                getResultKey={(message: any) => message.uid}
                renderResult={(message: any) => {
                  const { name, phone } = getSmsContactLabels(message, sms.bulkResolvedMap);

                  return (
                    <Box
                      onClick={() => handleSelectSms(message)}
                      sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider", cursor: "pointer", "&:hover": { bgcolor: "grey.50" } }}
                    >
                      <Box display="flex" alignItems="baseline" gap={0.75}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          {name || phone}
                        </Typography>
                        {name && (
                          <Typography variant="caption" color="text.disabled">
                            {phone}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" noWrap>
                        {highlightText(getSmsMessagePreview(message), trimmedQuery)}
                      </Typography>
                    </Box>
                  );
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
